<template>
  <div class="survey-topbar">
    <!-- Logo on the left -->
    <div class="topbar-left">
      <!-- Logo removed - displayed in sidebar -->
    </div>

    <!-- Buttons and user profile on the right -->
    <div class="topbar-right">
      <button class="btn-link" @click="goToSurveyList">
        Liste des enquêtes
      </button>
      <button class="btn-link" @click="addSurvey">Ajouter enquête</button>

      <b-dropdown right variant="link" no-caret class="user-dropdown">
        <template #button-content>
          <div class="user-profile-content">
            <div class="user-initials">{{ userInitials }}</div>
            <span class="user-name">{{ userName }}</span>
            <i class="bi bi-chevron-down"></i>
          </div>
        </template>
        <b-dropdown-item class="logout-item" @click="handleLogout">
          <i class="bi bi-box-arrow-right"></i>
          Déconnexion
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { displayName, initials } from '@/shared/utils/user-display';
import { surveyListRouteFor } from '@/shared/utils/survey-routes';

export default {
  name: 'SurveyTopBar',
  computed: {
    ...mapGetters('auth', ['user', 'userRole']),
    userName() {
      return displayName(this.user);
    },
    userInitials() {
      return initials(this.user);
    },
  },
  methods: {
    ...mapActions('auth', ['logoutLocal']),
    goToSurveyList() {
      // Le formulaire est ouvert aux trois roles: renvoyer tout le monde vers
      // "Mes enquetes" envoyait un Admin sur une page que son garde de route
      // lui refuse.
      this.$router.push({ name: surveyListRouteFor(this.userRole) });
    },
    addSurvey() {
      this.$router.push({ name: 'Survey' });
    },
    async handleLogout() {
      try {
        await this.logoutLocal();
        this.$router.push({ name: 'login' });
      } catch (error) {
        console.error('Error during logout:', error);
        this.$router.push({ name: 'login' });
      }
    },
  },
};
</script>

<style scoped>
.survey-topbar {
  background: white;
  border-bottom: none;
  /*
    Hauteur fixe et assumee: la barre est en position fixed, donc chaque page
    qui l'affiche doit reserver exactement cette hauteur - ProfessionalLayout
    et SurveyForm s'y referent.
  */
  height: 75px;
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: visible;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 1.5vw;
}

.brand-logo {
  height: 40px;
  width: auto;
  object-fit: contain;
}

.btn-link {
  background: none;
  border: none;
  color: #000000;
  font-size: 13px;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
}

.btn-link:hover {
  color: #005bd0;
}

.topbar-right {
  position: relative;
  display: flex;
  align-items: center;
  gap: 2vw;
  overflow: visible;
}

.user-dropdown ::v-deep .btn-link {
  padding: 0;
  background: none !important;
  border: none !important;
  box-shadow: none !important;
}

.user-dropdown ::v-deep .btn-link:hover,
.user-dropdown ::v-deep .btn-link:focus,
.user-dropdown ::v-deep .btn-link:active {
  background: #f9fafb !important;
  box-shadow: none !important;
}

.user-dropdown ::v-deep .dropdown-toggle::after {
  display: none !important;
}

.user-profile-content {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.user-dropdown ::v-deep .dropdown-menu {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: none;
  min-width: 180px;
  padding: 8px 0;
  margin-top: 8px;
}

.user-dropdown ::v-deep .dropdown-item {
  padding: 10px 16px;
  font-size: 14px;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-dropdown ::v-deep .dropdown-item:hover {
  background: #f3f4f6;
}

.user-dropdown ::v-deep .dropdown-item i {
  font-size: 16px;
  color: #6b7280;
}

.user-initials {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: #005bd0;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
  font-family:
    'Poppins',
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
}

.user-name {
  font-size: 13px;
  font-weight: 500;
  color: #000000;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
}

.user-profile i {
  font-size: 13px;
  color: #6b7280;
}

@media (max-width: 999px) {
  .survey-topbar {
    padding: 15px 20px;
  }

  .topbar-left {
    gap: 15px;
  }

  .brand-logo {
    height: 35px;
  }

  .topbar-right {
    gap: 20px;
  }

  .btn-link {
    font-size: 12px;
  }

  .user-profile {
    gap: 8px;
    padding: 6px 10px;
    border-radius: 6px;
  }

  .user-initials {
    width: 32px;
    height: 32px;
    font-size: 10px;
  }

  .user-name {
    font-size: 12px;
  }

  .user-profile i {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .survey-topbar {
    padding: 10px 15px;
  }

  .brand-logo {
    height: 30px;
  }

  .topbar-right {
    gap: 12px;
  }

  .btn-link {
    font-size: 11px;
  }

  .user-profile {
    gap: 6px;
    padding: 4px 8px;
  }

  .user-initials {
    width: 28px;
    height: 28px;
    font-size: 9px;
  }

  .user-name {
    display: none;
  }

  .user-profile i {
    font-size: 10px;
  }
}
</style>
